import React, { useState } from "react";
import { Search, Gem, Clock, Check, ExternalLink, Copy, CheckCheck, AlertTriangle, ArrowRight, ChevronDown } from "lucide-react";
import { useAccount } from "wagmi";
import { formatEther } from "viem";
import { useStakingData } from "../../../hooks/useStakingData";
import { NFTDetails } from "./NFTDetails";
import { Card } from "../../shared/Card";
import { cn, truncateString } from "../../../lib/utils";
import { CoolMode } from "../../ui/cool-mode";
import { motion, AnimatePresence } from "framer-motion";
import type { Collection } from "../../../types";

interface CollectionCardProps {
  collection: Collection;
  index: number;
  className?: string;
}

const TOKEN_ID_RANGES = {
  "Frostbyte": { min: 1, max: 3333 },
  "MONKEES": { min: 1, max: 2001 },
  "RILLAZ": { min: 1, max: 10000 },
  "TokenGators": { min: 1, max: 4000 },
  "GobsOnApe": { min: 1, max: 2222 },
  "Dengs": { min: 1, max: 4700 },
  "The Mich Khan": { min: 1, max: 2578 },
  "Gs on Ape": { min: 1, max: 5555 }
};

export function CollectionCard({ collection, index, className }: CollectionCardProps) {
  const [tokenId, setTokenId] = useState("");
  const [copied, setCopied] = useState(false);
  const [listingData, setListingData] = useState<{ isListed: boolean; price?: string } | null>(null);
  const [isCheckingListing, setIsCheckingListing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPool, setSelectedPool] = useState(collection.pools?.[0]);
  const [isHovered, setIsHovered] = useState(false);
  const { data: stakingInfo, isLoading } = useStakingData(collection, tokenId);

  const range = TOKEN_ID_RANGES[collection.name as keyof typeof TOKEN_ID_RANGES];
  const showMismatchWarning = collection.name === "Frostbyte";

  const handleTokenIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = parseInt(value);

    if (value === "") {
      setTokenId("");
      setError(null);
      return;
    }

    if (!range) {
      setError("Invalid collection");
      return;
    }

    if (isNaN(numValue)) {
      setError("Please enter a valid number");
      return;
    }

    if (numValue < range.min || numValue > range.max) {
      setError(`Token ID must be between ${range.min} and ${range.max}`);
      return;
    }

    setTokenId(value);
    setError(null);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenId || error) return;

    setIsCheckingListing(true);
    setListingData(null);

    try {
      const response = await fetch(`https://api-mainnet.magiceden.dev/v3/rtp/apechain/tokens/v6?collection=${collection.address}&tokens[]=${collection.address}:${tokenId}`);
      if (!response.ok) throw new Error('Failed to fetch token data');
      
      const data = await response.json();
      
      if (data.tokens?.[0]) {
        const token = data.tokens[0];
        setListingData({
          isListed: Boolean(token.market?.floorAsk?.price),
          price: token.market?.floorAsk?.price ? `${token.market.floorAsk.price} APE` : undefined
        });
      } else {
        setListingData({ isListed: false });
      }
    } catch (error) {
      console.error('Error fetching token data:', error);
      setListingData(null);
    } finally {
      setIsCheckingListing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(collection.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const poolId = e.target.value;
    const pool = collection.pools?.find(p => p.poolId === poolId);
    setSelectedPool(pool);
  };

  const magicEdenLink = `https://magiceden.us/collections/apechain/${collection.address}`;
  const placeholder = range ? `Enter Token ID (${range.min}-${range.max})` : "Enter Token ID";

  return (
    <Card
      motionProps={{
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: index * 0.1 },
        whileHover: { scale: 1.01 }
      }}
      className={cn(
        "backdrop-blur-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300",
        "bg-gradient-to-br from-[#0054fa]/10 via-[#002787]/10 to-[#030a21]/80",
        className
      )}
    >
      <motion.div 
        className="relative h-64 sm:h-72"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <motion.img 
        src={collection.image} 
        alt={collection.name} 
        className="w-full h-full object-cover rounded-t-2xl"
      />

        <motion.div 
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-t-2xl"
          animate={isHovered ? { opacity: 0.9 } : { opacity: 0.7 }}
          transition={{ duration: 0.3 }}
        />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex flex-col gap-4">
            <motion.div 
              className="flex items-center gap-3"
              whileHover={{ x: 5 }}
            >
              <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-white to-blue-400 bg-clip-text drop-shadow-lg">
                {collection.name}
              </h3>
              {collection.verified && (
                <motion.div
                  className="relative group"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-blue-500 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Verified Collection
                  </div>
                </motion.div>
              )}
            </motion.div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-base text-gray-200 drop-shadow-lg">
                  {truncateString(collection.address, 8)}
                </p>
                <motion.button
                  onClick={handleCopy}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copy address"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {copied ? (
                    <CheckCheck className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-300" />
                  )}
                </motion.button>
              </div>
              <CoolMode>
                <motion.a
                  href={magicEdenLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-xl text-blue-400 transition-colors interactive-button group"
                  whileHover={{ scale: 1.02 }}
                >
                  <span>View on Magic Eden</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </motion.div>
                </motion.a>
              </CoolMode>
            </div>
          </div>
        </div>
      </motion.div>

      <Card.Content className="space-y-6">
        {showMismatchWarning && (
          <motion.div 
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-400/90 text-sm">
                ID Mismatch warning: Frostbyte and Magic Eden IDs may not match
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {collection.pools ? (
            <motion.div 
              className="col-span-2 bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent rounded-xl p-6 hover:from-blue-500/15 hover:via-blue-500/10 transition-colors duration-300"
              whileHover={{ scale: 1.01 }}
            >
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-5 h-5" />
                    <span className="text-lg">Staking Duration</span>
                  </div>
                  <div className="relative inline-block w-40">
                    <select
                      value={selectedPool?.poolId}
                      onChange={handlePoolChange}
                      className="w-full bg-blue-500/20 text-white border border-blue-500/30 rounded-lg px-4 py-2 pr-10 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 hover:bg-blue-500/30 transition-colors cursor-pointer"
                      style={{ 
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none'
                      }}
                    >
                      {collection.pools.map(pool => (
                        <option key={pool.poolId} value={pool.poolId} className="bg-slate-900">
                          {pool.duration} Days
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </div>
                <motion.div 
                  className="flex items-center gap-3 text-white text-xl"
                  whileHover={{ x: 5 }}
                >
                  <Gem className="w-6 h-6 text-blue-400" />
                  <span>Reward: {Math.floor(Number(formatEther(BigInt(selectedPool?.reward || collection.reward))))} $GEM</span>
                </motion.div>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div 
                className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent rounded-xl p-6 hover:from-blue-500/15 hover:via-blue-500/10 transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-lg">Duration</span>
                </div>
                <div className="text-white text-xl font-medium">
                  {Math.floor(collection.duration / 86400)} days
                </div>
              </motion.div>
              <motion.div 
                className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent rounded-xl p-6 hover:from-blue-500/15 hover:via-blue-500/10 transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-2 text-gray-400 mb-2">
                  <Gem className="w-5 h-5" />
                  <span className="text-lg">Reward</span>
                </div>
                <div className="text-white text-xl font-medium">
                  {Math.floor(Number(formatEther(BigInt(collection.reward))))} $GEM
                </div>
              </motion.div>
            </>
          )}
        </div>

        <form onSubmit={handleSearch} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <motion.input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={tokenId}
              onChange={handleTokenIdChange}
              min={range?.min}
              max={range?.max}
              placeholder={placeholder}
              className="flex-1 bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-lg 
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent text-blue-100
                       hover:bg-blue-500/20 transition-all duration-300"
              whileFocus={{ scale: 1.01 }}
            />
            <CoolMode>
              <motion.button
                type="submit"
                disabled={isLoading || !tokenId || Boolean(error) || isCheckingListing}
                className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:hover:from-blue-500 disabled:hover:to-blue-600 
                         px-6 py-3 rounded-xl flex items-center justify-center gap-2 text-lg text-white transition-all duration-300 interactive-button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Search className="w-5 h-5" />
                {isCheckingListing ? 'Checking...' : 'Check'}
              </motion.button>
            </CoolMode>
          </div>
          {error && (
            <motion.p 
              className="text-red-400 text-sm px-1"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.p>
          )}
        </form>

        <AnimatePresence mode="wait">
          {listingData && (
            <motion.div 
              className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center">
                <span className="text-lg text-gray-400">Listing Status</span>
                <span className={cn(
                  "font-medium text-lg",
                  listingData.isListed ? "text-green-400" : "text-gray-400"
                )}>
                  {listingData.isListed ? `Listed for ${listingData.price}` : "Not Listed"}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {tokenId && !error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <NFTDetails collection={collection} tokenId={tokenId} />
            </motion.div>
          )}
        </AnimatePresence>
      </Card.Content>
    </Card>
  );
}